import "./JobTable.scss";

import {
  formatStatusLabel,
  getStatusVariant,
  TableCellStatusPill,
} from "components/TableCellStatusPill/TableCellStatusPill";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import { formatMinutes } from "utils/date-utils";
import { useCurrentUser } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { useUrlStore } from "utils/useUrlStore";

import JobTableModal from "./JobTableModal";

const COLUMN_NAMES: [string, string][] = [
  ["id", "ID"],
  ["file_id", "File ID"],
  ["created", "Started at"],
  ["last_status_update_at", "Last Status Update At"],
  ["duration", "Duration"],
  ["job_type", "Job Type"],
  ["status", "Status"],
  ["status_message", "Status Message"],
  ["started_by", "Started By"],
];

const JobTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<
    BackgroundJobDataType | undefined
  >();
  const { getFilters } = useUrlStore();
  const urlFilters = getFilters();

  const {
    data: background_jobs = { results: [], count: 0 },
    isFetching,
    isLoading,
  } = useGetBackgroundJobsQuery(
    {
      filters: {
        ...urlFilters,
        limit: PAGE_SIZE,
        offset: OFFSET(page),
      },
    },
    {
      skip: false,
    },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const handleRowClick = (row: BackgroundJobDataType) => {
    // only allow staff users to open the modal
    if (!currentUser?.is_staff) {
      return;
    }

    setSelectedJob(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedJob(undefined);
  };

  const rows =
    background_jobs?.results?.map((job: BackgroundJobDataType) => {
      let durationMinutes: number | null = null;

      if (job.duration !== null) {
        durationMinutes = job.duration > 0 ? job.duration : null;
      } else if (job.minutes_in_progress > 0) {
        durationMinutes = job.minutes_in_progress;
      }

      const formattedDuration = durationMinutes
        ? formatMinutes(durationMinutes)
        : "-";

      return {
        id: job.id,
        file_id: job.file_id,
        file_url: job.file_url,
        claim_id: job.claim_id,
        created: job.created,
        duration: formattedDuration,
        job_type: job.job_type,
        status: job.status,
        status_message: job.status_message,
        started_by: job.started_by,
        last_status_update_at: job.last_status_update_at ?? "",
      };
    }) || [];

  const FileIdRenderCell = ({ row }: { row: BackgroundJobDataType }) => {
    return (
      <span className="file-link" onClick={() => navigate(row.file_url)}>
        {row.file_id}
      </span>
    );
  };

  const StatusRenderCell = ({ row }: { row: BackgroundJobDataType }) => (
    <TableCellStatusPill
      label={formatStatusLabel(row.status)}
      variant={getStatusVariant(row.status)}
    />
  );

  const makeColumns = (keys: [string, string][]) =>
    keys.map(([key, name]) => ({
      key,
      name,
      ...commonColumnProps,
      renderCell:
        key === "file_id"
          ? FileIdRenderCell
          : key === "status"
            ? StatusRenderCell
            : undefined,
    }));

  const BACKGROUND_JOB_COLUMNS = makeColumns(COLUMN_NAMES);

  return (
    <div>
      <Table
        className="background-job-table"
        columns={BACKGROUND_JOB_COLUMNS}
        rows={rows as unknown as BackgroundJobDataType[]}
        rowKeyGetter={(row: BackgroundJobDataType) => row.id}
        emptyTableMessage="No background jobs"
        isLoading={isFetching}
        fullHeight
        onCellClick={({ row }) => handleRowClick(row)}
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: background_jobs.count,
          onPageChange: (pageIndex) => setPage(pageIndex),
          isLoading: isFetching,
        }}
      />

      <JobTableModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        initialData={selectedJob}
      />
    </div>
  );
};

export default JobTable;
